import React, { ReactNode } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import { useFormik } from 'formik';
import { useUpdateCompanyDetailsMutation } from 'services';
import toast from 'react-hot-toast';
import useUserStore from 'store/user';
import useTranslate from 'hooks/useTranslate';
import { CompanyAboutUsInterface } from 'constants/interfaces';
import { companyAboutUsInitialValues, companyAboutUsSchema } from 'constants/schemaValidations';
import ReactDatePicker from 'react-datepicker';
import { formatDateWithYear } from 'utils';
import useCommonStore from 'store/common';

const EditCompanyAboutUs: React.FC<any> = () => {
	const { profileData }: any = useUserStore((state) => state);
	const { modalConfig = {} }: any = useCommonStore((state) => state);
	const { mutateAsync: updateProfile } = useUpdateCompanyDetailsMutation();
	const { translate } = useTranslate();
	const {
		companyCEOName = '',
		companyDescription = '',
		companyFoundedYear = '',
		companySize = null,
	} = profileData;
	const formik: any = useFormik({
		initialValues: {
			...companyAboutUsInitialValues,
			companyCEOName,
			companyDescription,
			companyFoundedYear: formatDateWithYear(companyFoundedYear),
			companySize,
		},
		enableReinitialize: true,
		validationSchema: companyAboutUsSchema(translate),
		onSubmit: (values: any) => {
			updateProfile({
				...values,
				companyFoundedYear: values?.companyFoundedYear?.getFullYear(),
				companyDescription: values?.companyDescription.trim(),
				saveType: 'aboutCompany',
			})
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
					}
				})
				.finally(() => modalConfig?.onClose());
		},
	});

	const renderError = <T extends keyof CompanyAboutUsInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => modalConfig?.onClose()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Profile.editCompanyOverview')}</h4>
					<Row>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.ceo')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterName')}
									className="rounded-pill"
									min={1}
									name="companyCEOName"
									onChange={(e: any) => {
										let inputValue = e.target.value;

										// Remove leading spaces if present
										inputValue = inputValue.replace(/^\s+/, '');

										// Allow only one space between words
										inputValue = inputValue.replace(/\s{2,}/g, ' ');
										formik.setFieldValue('companyCEOName', inputValue);
									}}
									onBlur={formik.handleBlur}
									value={formik.values.companyCEOName}
									autoComplete="off"
								/>
								{renderError('companyCEOName')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.foundedYear')}</Form.Label>
								<ReactDatePicker
									selected={formik.values.companyFoundedYear}
									onChange={(date: any) => formik.setFieldValue('companyFoundedYear', date)}
									name="companyFoundedYear"
									onBlur={formik.handleBlur}
									dateFormat="yyyy"
									showYearPicker
									placeholderText={`${translate('PlaceHolder.onlyYear')}`}
								/>
								{renderError('companyFoundedYear')}
							</Form.Group>
						</Col>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.companySize')}</Form.Label>
								<Form.Control
									type="number"
									placeholder={translate('PlaceHolder.enterCompanySize')}
									className="rounded-pill"
									min={1}
									name="companySize"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.companySize}
									autoComplete="off"
								/>
								{renderError('companySize')}
							</Form.Group>
						</Col>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.editAbout')}</Form.Label>
								<Form.Control
									name="companyDescription"
									as="textarea"
									rows={4}
									placeholder={translate('Common.addAboutDescription')}
									onChange={(e) => {
										let inputValue = e.target.value;

										// Remove leading spaces if present
										inputValue = inputValue.replace(/^\s+/, '');

										// Allow only one space between words
										inputValue = inputValue.replace(/\s{2,}/g, ' ');
										formik.setFieldValue('companyDescription', inputValue);
									}}
									onBlur={formik.handleBlur}
									value={formik.values.companyDescription}
									autoComplete="off"
								/>
								{renderError('companyDescription')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						<Button className="filled-btn rounded-pill" type="submit">
							{translate('Common.save')}
						</Button>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default EditCompanyAboutUs;
